import React from 'react';
import { motion } from 'framer-motion';
import { Heart } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Language } from '../App';
// Custom PDF document icon
const PdfIcon = ({
  className = 'w-5 h-5'
}: {
  className?: string;
}) => <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" data-id="element-456">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" data-id="element-457" />
    <polyline points="14 2 14 8 20 8" data-id="element-458" />
    <text x="12" y="17.5" textAnchor="middle" fontSize="5.5" fontWeight="700" stroke="none" fill="currentColor" style={{
    fontFamily: 'system-ui, sans-serif'
  }} data-id="element-459">
      PDF
    </text>
  </svg>;
const MAY_JUN_2026_PDF_URL = 'https://www.dropbox.com/scl/fi/9d258gbuccg8n33xhhu08/May-June-2026.pdf?rlkey=nw1rxu9sh5gkirm29nnv4kt19&st=7j3rnhxb';
interface CurrentIssuePageProps {
  language: Language;
}
export function CurrentIssuePage({
  language
}: CurrentIssuePageProps) {
  const isPashto = language === 'pashto';
  const fadeIn = {
    initial: {
      opacity: 0,
      y: 20
    },
    whileInView: {
      opacity: 1,
      y: 0
    },
    viewport: {
      once: true,
      margin: '-50px' as const
    },
    transition: {
      duration: 0.6
    }
  };
  return <div className="pt-20 md:pt-24 bg-gray-50 min-h-screen pb-0" data-id="element-460">
      {/* Section 1: Issue Header */}
      <section className="relative bg-[#1a5c5c] py-8 md:py-12 px-6 overflow-hidden" data-id="element-461">
        <svg className="absolute right-0 top-0 w-1/2 h-full pointer-events-none opacity-5" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="xMaxYMid meet" viewBox="0 0 1200 400" data-id="element-462">
          <g fill="white" data-id="element-463">
            <path d="M820,145 C810,130 795,120 780,115 C790,125 795,135 800,142 C790,138 775,132 760,130 C778,138 790,142 800,145 C795,148 790,155 785,165 C795,155 800,148 805,145 C810,148 815,155 820,165 C815,155 812,148 810,145 C820,142 835,138 850,130 C835,132 820,138 810,142 C815,135 820,125 830,115 Z" transform="translate(200,50) scale(1.5)" data-id="element-464" />
          </g>
        </svg>

        <div className="relative z-10 max-w-4xl mx-auto flex flex-col items-center text-center" data-id="element-465">
          {/* Logo */}
          <motion.div initial={{
          opacity: 0,
          scale: 0.9
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.5
        }} className="w-20 h-20 md:w-24 md:h-24 rounded-full overflow-hidden border-2 border-white/20 shadow-xl mb-6 flex-shrink-0 bg-white" data-id="element-466">
            <img src="https://cdn.magicpatterns.com/uploads/a1wFSzqfrcuN2xyXncDR6P/Pasoon.png" alt="Pasoon Logo" className="w-full h-full object-contain" data-id="element-467" />
          </motion.div>

          {/* Title */}
          <motion.h1 initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          delay: 0.1
        }} className={`text-3xl md:text-5xl font-bold text-white mb-2 ${isPashto ? 'font-pashto' : 'font-serif'}`} data-id="element-468">
            {isPashto ? 'مۍ – جون ۲۰۲۶' : 'May – June 2026'}
          </motion.h1>

          {/* Subtext */}
          <motion.p initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          delay: 0.2
        }} className={`text-white/70 text-lg md:text-xl font-medium tracking-wide ${isPashto ? 'font-pashto' : ''}`} data-id="element-469">
            {isPashto ? 'ټوک ۲۱' : 'Volume 21'}
          </motion.p>

          {/* Color Strip */}
          <motion.div initial={{
          opacity: 0,
          scaleX: 0
        }} animate={{
          opacity: 1,
          scaleX: 1
        }} transition={{
          duration: 0.7,
          delay: 0.3
        }} className="flex w-48 md:w-64 h-1.5 mt-8 rounded-full overflow-hidden" data-id="element-470">
            <div className="flex-1 bg-[#C62828]" data-id="element-471"></div>
            <div className="flex-1 bg-[#F9A825]" data-id="element-472"></div>
            <div className="flex-1 bg-[#2E7D32]" data-id="element-473"></div>
          </motion.div>
        </div>
      </section>

      {/* Section 2: Featured Article Block */}
      <section className="py-8 md:py-12 px-6" data-id="element-474">
        <div className="max-w-3xl mx-auto" data-id="element-475">
          <motion.div {...fadeIn} className="bg-white rounded-2xl shadow-xl shadow-gray-200/40 border border-gray-100 p-8 md:p-12" data-id="element-476">
            <h2 className={`text-2xl md:text-3xl font-bold text-pasoon-dark text-center mb-8 ${isPashto ? 'font-pashto' : 'font-serif'}`} data-id="element-477">
              {isPashto ? 'په پښتو لیکنه کې توپیرونه' : 'Differences in Pashto Writing'}
            </h2>

            <div className="space-y-8" data-id="element-478">
              {isPashto ? <>
                  {/* Pashto First */}
                  <div className="text-right" dir="rtl" data-id="element-479">
                    <p className="text-gray-700 font-pashto leading-loose text-lg" data-id="element-480">
                      پښتو ژبه بېلابېلې لهجې او د لیکلو ډولونه لري. په کوټه،
                      کندهار او افغانستان کې د لیکلو یو ډول کارول کیږي، حال دا
                      چې په خیبر پښتونخوا کې د یوسفزو د لیکلو سټایل لږ توپیر
                      لري. نن ورځ، کمپیوټرونه، ګوګل او مصنوعي ذکا ډېری وخت د
                      کندهاري سټایل معیار کاروي، نو ځکه د یوسفزو سټایل په ډیجیټل
                      نړۍ کې وروسته پاتې شوی دی.
                    </p>

                    <h3 className="font-pashto font-bold text-pasoon-red text-xl mt-6 mb-3" data-id="element-481">
                      ګټې:
                    </h3>
                    <ol className="font-pashto leading-loose text-gray-700 text-lg space-y-2 list-decimal pr-6" data-id="element-482">
                      <li data-id="element-483">
                        په ټوله نړۍ کې پښتانه به د یوه معیاري سټایل سره ولولي او
                        ولیکي
                      </li>
                      <li data-id="element-484">
                        کتابونه، کیبورډونه او سافټویر به د جوړولو او کارولو
                        لپاره اسانه شي
                      </li>
                      <li data-id="element-485">راتلونکې نسلونه به د یوې ګډې لیکنې سره بلد شي</li>
                    </ol>

                    <h3 className="font-pashto font-bold text-pasoon-red text-xl mt-6 mb-3" data-id="element-486">
                      زیانونه:
                    </h3>
                    <ol className="font-pashto leading-loose text-gray-700 text-lg space-y-2 list-decimal pr-6" data-id="element-487">
                      <li data-id="element-488">
                        ځینې د یوسفزو توري او کلمې به د وخت په تېرېدو سره ورک شي
                      </li>
                      <li data-id="element-489">د سیمه ییز پښتو هویت کمزوری کېدای شي</li>
                      <li data-id="element-490">د پښتو تنوع او بډایه والی به کم شي</li>
                    </ol>

                    <p className="font-pashto leading-loose text-gray-700 text-lg mt-6" data-id="element-491">
                      یوه ښه حل لاره دا ده چې د سیمه ییزو پښتو سټایلونو او
                      دودونو ساتلو سره د لیکنې یو معیاري سیسټم ولرو.
                    </p>
                  </div>
                  <div className="w-full h-px bg-gray-200" data-id="element-492"></div>
                  {/* English Second */}
                  <div className="text-left" dir="ltr" data-id="element-493">
                    <p className="text-gray-600 leading-relaxed text-base" data-id="element-494">
                      Pashto language has different dialects and writing styles.
                      In Quetta, Kandahar and Afghanistan, one style of writing
                      is used while in Khyber Pakhtunkhwa, the Yousafzai writing
                      style is slightly different. Today, computers, Google and
                      AI mostly use the Kandahari-style standard, therefore the
                      Yousafzai style has been left behind in the digital world.
                    </p>

                    <h3 className="font-bold text-pasoon-red text-lg mt-5 mb-2" data-id="element-495">
                      Benefits:
                    </h3>
                    <ol className="leading-relaxed text-gray-600 space-y-1.5 list-decimal pl-6" data-id="element-496">
                      <li data-id="element-497">
                        Pashtuns around the world will read and write in one
                        standard style
                      </li>
                      <li data-id="element-498">
                        Books, keyboards and software will become easier to
                        develop and use
                      </li>
                      <li data-id="element-499">
                        Future generations will become familiar with one common
                        script
                      </li>
                    </ol>

                    <h3 className="font-bold text-pasoon-red text-lg mt-5 mb-2" data-id="element-500">
                      Disadvantages:
                    </h3>
                    <ol className="leading-relaxed text-gray-600 space-y-1.5 list-decimal pl-6" data-id="element-501">
                      <li data-id="element-502">
                        Some Yousafzai letters and words may disappear over time
                      </li>
                      <li data-id="element-503">Regional Pashto identity may become weaker</li>
                      <li data-id="element-504">The diversity and richness of Pashto may decrease</li>
                    </ol>

                    <p className="leading-relaxed text-gray-600 mt-5" data-id="element-505">
                      A good solution is to have one standard writing system
                      while preserving regional Pashto styles and traditions.
                    </p>
                  </div>
                </> : <>
                  {/* English First */}
                  <div className="text-left" dir="ltr" data-id="element-506">
                    <p className="text-gray-700 leading-relaxed text-lg" data-id="element-507">
                      Pashto language has different dialects and writing styles.
                      In Quetta, Kandahar and Afghanistan, one style of writing
                      is used while in Khyber Pakhtunkhwa, the Yousafzai writing
                      style is slightly different. Today, computers, Google and
                      AI mostly use the Kandahari-style standard, therefore the
                      Yousafzai style has been left behind in the digital world.
                    </p>

                    <h3 className="font-bold text-pasoon-red text-xl mt-6 mb-3" data-id="element-508">
                      Benefits:
                    </h3>
                    <ol className="leading-relaxed text-gray-700 text-lg space-y-2 list-decimal pl-6" data-id="element-509">
                      <li data-id="element-510">
                        Pashtuns around the world will read and write in one
                        standard style
                      </li>
                      <li data-id="element-511">
                        Books, keyboards and software will become easier to
                        develop and use
                      </li>
                      <li data-id="element-512">
                        Future generations will become familiar with one common
                        script
                      </li>
                    </ol>

                    <h3 className="font-bold text-pasoon-red text-xl mt-6 mb-3" data-id="element-513">
                      Disadvantages:
                    </h3>
                    <ol className="leading-relaxed text-gray-700 text-lg space-y-2 list-decimal pl-6" data-id="element-514">
                      <li data-id="element-515">
                        Some Yousafzai letters and words may disappear over time
                      </li>
                      <li data-id="element-516">Regional Pashto identity may become weaker</li>
                      <li data-id="element-517">The diversity and richness of Pashto may decrease</li>
                    </ol>

                    <p className="leading-relaxed text-gray-700 text-lg mt-6" data-id="element-518">
                      A good solution is to have one standard writing system
                      while preserving regional Pashto styles and traditions.
                    </p>
                  </div>
                  <div className="w-full h-px bg-gray-200" data-id="element-519"></div>
                  {/* Pashto Second */}
                  <div className="text-right" dir="rtl" data-id="element-520">
                    <p className="text-gray-600 font-pashto leading-loose text-base" data-id="element-521">
                      پښتو ژبه بېلابېلې لهجې او د لیکلو ډولونه لري. په کوټه،
                      کندهار او افغانستان کې د لیکلو یو ډول کارول کیږي، حال دا
                      چې په خیبر پښتونخوا کې د یوسفزو د لیکلو سټایل لږ توپیر
                      لري. نن ورځ، کمپیوټرونه، ګوګل او مصنوعي ذکا ډېری وخت د
                      کندهاري سټایل معیار کاروي، نو ځکه د یوسفزو سټایل په ډیجیټل
                      نړۍ کې وروسته پاتې شوی دی.
                    </p>
                  </div>
                </>}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Section 3: Cover Image Display */}
      <section className="py-8 md:py-10 px-6 bg-gray-100" data-id="element-522">
        <div className="max-w-4xl mx-auto text-center" data-id="element-523">
          <motion.div {...fadeIn} className="inline-block bg-white p-3 md:p-4 rounded-xl shadow-lg shadow-gray-300/50 border border-gray-200" data-id="element-524">
            <img src="https://cdn.magicpatterns.com/uploads/dpcJ11jnpAvCh6Q2HiDXDa/Front.jpg" alt="Pasoon May-June 2026 Cover" className="w-full max-w-2xl h-auto object-contain rounded" data-id="element-525" />
          </motion.div>
          <motion.p {...fadeIn} className={`mt-6 text-gray-500 text-sm md:text-base font-medium ${isPashto ? 'font-pashto' : ''}`} data-id="element-526">
            {isPashto ? 'علمي، سیاسي او کلتوري مرکز ... اسلامیه کالج پېښور' : 'Academic, Political and Cultural Centre — Islamia College Peshawar'}
          </motion.p>
        </div>
      </section>

      {/* Section 5: Call to Action */}
      <section className="py-8 md:py-12 px-6 bg-white" data-id="element-527">
        <div className="max-w-3xl mx-auto" data-id="element-528">
          <motion.div {...fadeIn} className="flex flex-col sm:flex-row items-center justify-center gap-4 md:gap-6" data-id="element-529">
            <a href={`${MAY_JUN_2026_PDF_URL}&dl=1`} className={`w-full sm:w-auto flex items-center justify-center gap-2 px-8 py-4 bg-pasoon-red text-white rounded-xl font-bold text-lg hover:bg-red-800 transition-all shadow-lg shadow-red-900/20 active:scale-[0.98] ${isPashto ? 'font-pashto' : ''}`} data-id="element-530">
              <PdfIcon className="w-5 h-5" data-id="element-531" />
              {isPashto ? 'PDF ډاونلوډ کړئ' : 'Download PDF'}
            </a>

            <Link to="/subscription" className={`w-full sm:w-auto flex items-center justify-center gap-2 px-6 py-4 text-pasoon-muted font-bold text-lg hover:text-green-800 transition-colors ${isPashto ? 'font-pashto' : ''}`} data-id="element-532">
              <Heart className="w-5 h-5" data-id="element-533" />
              {isPashto ? 'پاڅون سره مرسته وکړئ' : 'Support Pasoon'}
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Section 4: Issue Information Footer Block */}
      <section className="bg-[#1a1a1a] relative" data-id="element-534">
        <div className="flex w-full h-1.5" data-id="element-535">
          <div className="flex-1 bg-[#C62828]" data-id="element-536"></div>
          <div className="flex-1 bg-[#F9A825]" data-id="element-537"></div>
          <div className="flex-1 bg-[#2E7D32]" data-id="element-538"></div>
        </div>

        <div className="max-w-4xl mx-auto px-6 py-10 md:py-12" data-id="element-539">
          <div className="flex flex-col md:flex-row items-center md:items-start justify-between gap-8" data-id="element-540">
            <div className="text-center md:text-start space-y-3" data-id="element-541">
              <p className={`text-white/90 font-bold text-lg ${isPashto ? 'font-pashto' : ''}`} data-id="element-542">
                {isPashto ? 'خپرندوی: پاڅون خپرندویه ادارې، مردان' : 'Published by: Pasoon Publishers, Mardan'}
              </p>
              <div className="space-y-1 text-white/60 text-sm" data-id="element-543">
                <p data-id="element-544">Tel & Fax: 0937 – 874489</p>
                <p data-id="element-545">Email: pasoonmardan@gmail.com</p>
                <p data-id="element-546">Website: www.pasoon.org</p>
              </div>
            </div>

            <div className="w-16 h-16 rounded-full overflow-hidden border-2 border-white/10 bg-white flex-shrink-0" data-id="element-547">
              <img src="https://cdn.magicpatterns.com/uploads/a1wFSzqfrcuN2xyXncDR6P/Pasoon.png" alt="Pasoon Logo" className="w-full h-full object-contain" data-id="element-548" />
            </div>
          </div>
        </div>
      </section>
    </div>;
}